import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import debounceRender from 'react-debounce-render';
import { analytics } from 'core/analytics';
import { useRouterStore, useClientStore } from 'apps/hooks';
import { useSharedFileManager } from 'apps/share/hooks';
import { useContextManager } from 'apps/context/hooks';
import { useChatControl } from 'libs/rendering/hooks';
// UI config
import { UiModes, UiPosition } from 'libs/rendering/gltf/config';
// general components
import Switcher from 'libs/rendering/gltf/components/switcher/switcher';
import { TabsWrapperStyled } from 'libs/rendering/gltf/viewer.styled';
import { Button, Box, BottomNav, BottomNavItem } from 'components';
import { ButtonGroup } from 'apps/components/shared';
import { CADSidebar } from 'apps/components/Frame/FrameSidebar';

import { Canvas3D } from 'libs/rendering/gltf/components/canvas3D';
import { PinNotes } from 'libs/rendering/gltf/components/pin';
import { GltfViewerReload } from 'libs/rendering/gltf/api/errors';
import { useCADManager } from 'hooks';
import { CADComparisonHeight } from 'styles/constants';

// viewer components
// import UserSettings from 'libs/rendering/components/header/userSettings';
import { SystemMessage } from './components/systemMessage';
import { TabManager } from './components/tab';
import InfoSection from './components/InfoSection';
import LeftPanels from './components/leftPanels/leftPanels';
import LightModal from './components/lightModal/lightModal';
// import NoAccess from './components/lightModal/blocks/noAccess';
// import TouchBlock from './components/lightModal/blocks/touchBlock';
import TAB_NAME from './tabName';

import { GltfLoading } from './components/loading';
import { ToolBar } from './components/toolBar';

const SystemMessageDebounced = debounceRender(SystemMessage, 100);

export const makeLoadingMessage = (progress, status, reason, errorCode) => {
  let loadingMessage = null;

  if (progress) {
    let message = progress;
    switch (progress) {
      case 'SCHEDULED': {
        message = 'Queued...';
        break;
      }
      case 'STARTING': {
        message = 'Initializing...';
        break;
      }
      case 'LOADING': {
        message = 'Importing...';
        break;
      }
      case 'IMPORTING': {
        message = 'Reading...';
        break;
      }
      case 'PROCESSING': {
        message = 'Processing...';
        break;
      }
      case 'TRANSFERING': {
        message = 'Streaming...';
        break;
      }
      default:
        message = progress;
    }
    loadingMessage = `${message}`;
  }

  if (status === 'ERROR' && reason !== '') {
    loadingMessage = `${reason}: ${errorCode}`;
  }

  return loadingMessage;
};

const GltfViewer = observer(function _GltfViewer(props) {
  const routerStore = useRouterStore();
  const contextManager = useContextManager();
  const sharedFileManager = useSharedFileManager();
  const chatControl = useChatControl();
  const { viewerControl, panelAndSidebarWidth, totalHeaderHeight } =
    useCADManager();
  const clientStore = useClientStore();

  const isMobileViewport = clientStore.isMobile;

  const { isPreview, width, cadId, partId, prodId } = props;
  const canvasWidth = width;

  const viewer = viewerControl?.viewer;
  const isDiffing = viewer?.isDiffLoading || viewer?.isDiffing;

  const { route } = routerStore;
  const { diff } = route.params;

  const file = contextManager?.viewerFile || props.file;
  const { status, reason, errorCode } =
    contextManager?.viewerConversion || props.file.conversionGltfjson;
  const { progress } = contextManager?.viewerConversionProgress || {
    progress: undefined,
  };

  const diffFile = contextManager?.diffFile;
  const {
    status: diffStatus,
    reason: diffReason,
    errorCode: diffErrorCode,
  } = contextManager?.diffConversion || {
    status: undefined,
    reason: undefined,
    errorCode: undefined,
  };
  const { progress: diffProgress } = contextManager?.diffConversionProgress || {
    progress: undefined,
  };

  const escKeyHandler = e => {
    if (!viewerControl) {
      return;
    }
    if (e?.key === 'Escape') {
      viewerControl.Escape();
    }
  };

  const shortcutsHandler = e => {
    if (!viewerControl) return;
    if (e?.ctrlKey) {
      // CTRL + something...
      if (e?.altKey) {
        // CTRL + ALT + something...
        if (e?.key === 'A' || e?.key === 'a') {
          viewerControl.debugFeatures = true;
        }
      }
    }
  };

  /*
    effects
  */

  const [timeoutFlag, setTimeoutFlag] = useState(false);
  useEffect(() => {
    // - we setTimeoutFlag(false) here so that every time we receive
    // a progress update we reset the timeoutFlag, this way we might
    // present an error after 10 minutes, but if for some reason we
    // still receive an update after 10 minutes the loading process
    // will continue as usual
    setTimeoutFlag(false);

    // - we only care about status === 'SUCCESS' to be able to load
    // the file, $progress does not really matter, so we run on
    // changes to $progress, but check the STATUS to determine if
    // we need to timeout or not
    let timer = null;
    if (status !== 'SUCCESS') {
      timer = setTimeout(() => {
        setTimeoutFlag(true);
      }, 10 * 60000); // 10 minutes
    }
    return () => {
      // important to clean these, this will stop the previously created
      // setTimeout each time we receive a progress update, and as long
      // as status !=== 'SUCCESS', a new setTimeout is trigger right after
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [progress, status]);

  useEffect(() => {
    window.addEventListener('keydown', escKeyHandler, true);
    window.addEventListener('keydown', shortcutsHandler, false);
    return () => {
      window.removeEventListener('keydown', escKeyHandler, true);
      window.removeEventListener('keydown', shortcutsHandler, false);
      viewerControl?.Unmount();
    };
  }, []);

  // file update
  useEffect(() => {
    if (viewerControl) {
      viewerControl.Mount(file, status, contextManager);
    }
  }, [file, status]);

  // diff file update
  useEffect(() => {
    if (
      viewerControl &&
      diffFile &&
      viewerControl.viewerReady &&
      diffStatus === 'SUCCESS'
    ) {
      viewerControl.loadDiffCadFile(diffFile);
    }
  }, [viewerControl.viewerReady, diffFile, diffStatus]);

  useEffect(() => {
    if (!diff && isDiffing) {
      viewer?.stopDiffing();
    } else if (diff && !isDiffing) {
      viewer?.startDiffing();
    }
  }, [diff, isDiffing, viewer]);

  // URL sync
  // - this should not run on changes to prodId or partId, it is only supposed
  // to run once, when eventsReady changes from false to true
  // - there is a potential for a recursive route updates here because the Inspect
  // functions call route navigate
  useEffect(() => {
    if (viewerControl && viewerControl.eventsReady) {
      if (cadId || partId) {
        viewerControl.InspectById(cadId || partId);
      } else if (prodId === 0 || prodId) {
        viewerControl.InspectProductById(prodId);
      }
    }
  }, [viewerControl.eventsReady]);

  let loadingMessage = 'Preparing CAD Room...';
  if (viewerControl?.isContextLost()) {
    loadingMessage = 'Restoring webGL context';
  }

  // - the current if (!file) shortcircuits error handling, which is why you end
  // up with infinitely loading UIs that actually errored
  // - instead we should be looking at flags like so:
  // if (!file && loading) return <GltfLoading />
  // if (!file && error) return <Error error={error} />
  // return <FileViewer file={file} />
  // - so then we can assume that file && viewerControl are defined in here
  if (!viewerControl || !file) {
    return (
      <GltfLoading
        isLayoutCentered
        visible
        text={loadingMessage}
        canvasWidth={canvasWidth}
        paused={true}
      />
    );
  }

  /*
    render
  */

  loadingMessage = makeLoadingMessage(progress, status, reason, errorCode);

  const handleTimeoutReload = () => {
    analytics.logError(
      new GltfViewerReload('user requested reload after timeout'),
    );
    window.location.reload();
  };

  if (timeoutFlag && status !== 'SUCCESS' && status !== 'ERROR') {
    return (
      <>
        <SystemMessage status='TIMEOUT' canvasWidth={canvasWidth} />
        <GltfLoading
          isLayoutCentered
          visible
          text={loadingMessage}
          canvasWidth={canvasWidth}
          paused={true}
        >
          <ButtonGroup spacing='small' alignment='center'>
            <Button
              outline
              text='Reload'
              size='small'
              onClick={handleTimeoutReload}
            />
          </ButtonGroup>
        </GltfLoading>
      </>
    );
  }

  if (viewerControl.viewerFailed || viewerControl.conversionProblem) {
    viewerControl.viewerVisible = false;
    let failedStatus = 'ERROR';
    if (viewerControl.viewerFailed) {
      failedStatus = 'EXCEPTION';
    }
    return (
      <>
        <SystemMessage status={failedStatus} canvasWidth={canvasWidth} />
        <GltfLoading
          isLayoutCentered
          visible
          text={loadingMessage}
          canvasWidth={canvasWidth}
          paused={true}
        />
      </>
    );
  }

  const paused = status === 'ERROR';
  if (status !== 'SUCCESS') {
    // - the viewer sets viewerVisible as soon as it starts rendering, which
    // is before the connection can fail
    // - so for now I just set viewerVisible back to false when an error happens,
    // this does result in a bit of flicker, but is a lot easier to deal with
    // right now
    viewerControl.viewerVisible = false;
    return (
      <>
        <SystemMessage
          status={status}
          progress={progress}
          canvasWidth={canvasWidth}
        />
        <GltfLoading
          isLayoutCentered
          visible
          text={loadingMessage}
          canvasWidth={canvasWidth}
          paused={paused}
        />
      </>
    );
  }

  let activeTab = TAB_NAME.HOME;

  // if (sharedFileManager?.userSettingsOpen) activeTab = TAB_NAME.SHARE;
  if (chatControl?.chatVisible) activeTab = TAB_NAME.DISCUSS;
  if (routerStore.route.params.panel === 'rfq') activeTab = TAB_NAME.MAKE;

  const handleHomeBtnClick = () => {
    if (sharedFileManager) sharedFileManager.SetUserSettingsOpen(false);

    const newParams = Object.assign({}, routerStore.route.params);
    delete newParams.panel;
    routerStore.navigate(routerStore.route.name, newParams, {
      replace: true,
    });
  };

  const handleChatBtnClick = () => {
    if (sharedFileManager) sharedFileManager.SetUserSettingsOpen(false);

    const newParams = Object.assign({}, routerStore.route.params);
    newParams.panel = 'discuss';
    routerStore.navigate(routerStore.route.name, newParams, {
      replace: true,
    });
  };

  // const handleShareBtnClick = () => {
  //   if (!sharedFileManager) return;
  //   analytics.sendEvent('invite-share-file');
  //   analytics.sendEvent('invite-share-file-CAD-rooms');
  //   const newParams = Object.assign({}, routerStore.route.params);
  //   delete newParams.panel;
  //   routerStore.navigate(routerStore.route.name, newParams, {
  //     replace: true,
  //   });
  //   sharedFileManager.SetUserSettingsOpen(true);
  // };

  const handleMakeBtnClick = () => {
    analytics.sendEvent('make-part-click-cad-rooms', {}, { ga: true });

    if (sharedFileManager) sharedFileManager.SetUserSettingsOpen(false);

    routerStore.navigate(routerStore.route.name, {
      ...routerStore.route.params,
      panel: 'rfq',
    });
  };

  const handleSwitcherClick = () => {
    viewerControl?.SetLightModalVisibility(true);
    viewerControl?.UpdateUiMode(UiModes.collapsed);
  };

  const onModalCloseBtnClick = () => {
    viewerControl?.SetLightModalVisibility(false);
  };

  const topGap = 8;

  return (
    <>
      <SystemMessageDebounced status={status} canvasWidth={canvasWidth} />
      {(!viewerControl.cadReady || !viewerControl.viewerVisible) && (
        <GltfLoading
          isLayoutCentered
          visible
          text={
            viewerControl.downloadPerc >= 0
              ? `${viewerControl.downloadPerc}%`
              : ''
          }
          canvasWidth={canvasWidth}
          paused={false}
        />
      )}

      {!isPreview && !diff && !diffFile && file && (
        <CADSidebar isPreview={isPreview} />
      )}

      {!isPreview && viewerControl.viewerVisible && <TabManager />}

      {viewerControl.isDiffLoading && diffStatus !== 'SUCCESS' && (
        <>
          <SystemMessage
            status={diffStatus}
            progress={diffProgress}
            top={`${totalHeaderHeight - CADComparisonHeight + topGap}px`}
            canvasWidth='150%'
          />
          <GltfLoading
            isLayoutCentered
            visible
            aboveEverything
            text={
              makeLoadingMessage(
                diffProgress,
                diffStatus,
                diffReason,
                diffErrorCode,
              ) || 'Loading...'
            }
            canvasWidth='150%'
            paused={false}
          />
        </>
      )}
      {!isPreview && viewerControl.viewerVisible && (
        <TabsWrapperStyled
          tabHeight={UiPosition.top}
          left={panelAndSidebarWidth}
          top={totalHeaderHeight}
          data-testid='CADPage-assemblyWrapper'
        >
          <TabManager />
        </TabsWrapperStyled>
      )}
      {!isPreview && viewerControl.viewerVisible && <LeftPanels />}
      {!isPreview && viewerControl.viewerVisible && <ToolBar />}
      {!isPreview && viewerControl.viewerVisible && <PinNotes />}
      {!isPreview && viewerControl.viewerVisible && <InfoSection />}
      {isPreview && <Canvas3D />}

      {/* mobile */}
      <LightModal
        mobile={viewerControl?.lightModalContentIndex !== 3}
        isOpen={viewerControl?.lightModalVisible}
        onCloseBtnClick={onModalCloseBtnClick}
      >
        <ToolBar mobileMode={true} />
      </LightModal>
      <Switcher onClickAction={handleSwitcherClick} />
      {isMobileViewport ? (
        <Box>
          <BottomNav
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              zIndex: 9999,
              width: '100vw',
            }}
            value={activeTab}
          >
            <BottomNavItem
              onClick={handleHomeBtnClick}
              value={TAB_NAME.HOME}
              iconName='home'
              label='Home'
            />
            {/* <BottomNavItem
              onClick={handleShareBtnClick}
              value={TAB_NAME.SHARE}
              iconName='user_plus'
              label='Share'
            /> */}
            <BottomNavItem
              onClick={handleChatBtnClick}
              value={TAB_NAME.DISCUSS}
              iconName='chat_multiple'
              label='Discuss'
            />
            {!isDiffing && (
              <BottomNavItem
                onClick={handleMakeBtnClick}
                value='make'
                iconName='factory'
                label='Make'
              />
            )}
          </BottomNav>
        </Box>
      ) : null}
    </>
  );
});

export default GltfViewer;
